/**
 * Which settings edit in a small panel beside their fact, and when a panel hands over to the full command dialog
 * (One testing backlog P8). A setting's edit button opens a panel holding the command's one editable field; Save runs the
 * dry run and applies the change at once when it is quiet, and otherwise the panel grows into the dialog, carrying what
 * was typed. Everything else (a command with several fields, a destructive one, one that asks before it takes any
 * input) opens the dialog directly.
 */
import type { DryRun, ManifestCommand } from "./contract";
import { fieldsOf, type FieldDescriptor } from "./widgets";

/** The widgets a panel can hold: one control that fits a popover, or the typeahead's list under it. */
const PANEL_KINDS: readonly FieldDescriptor["kind"][] = [
  "text",
  "textarea",
  "number",
  "money",
  "date",
  "datetime",
  "select",
  "entity",
  "suggest",
];

/**
 * The command's single editable field when a panel can hold it, else null (open the dialog). Judged from the manifest
 * alone: a destructive command, no field or several (the platform fields and the bulk targets do not count), a field
 * that only shows beside another, a picker of several, or a widget too large for a popover (a table, an address, an
 * upload) all open the dialog.
 */
export function panelFieldOf(
  declared: ManifestCommand,
): FieldDescriptor | null {
  if (declared.destructive) return null;
  return panelFieldAmong(
    fieldsOf(declared.request_schema ?? null, { hidden: new Set() }),
  );
}

/**
 * The same judgement on the fields the run draws once the first dry run has answered: a dry run can hide a field (a
 * choice's key, a guard's field), so a command with two declared fields can turn out to ask for one.
 */
export function panelFieldAmong(
  fields: readonly FieldDescriptor[],
): FieldDescriptor | null {
  if (fields.length !== 1) return null;
  const field = fields[0]!;
  if (field.showIf !== null) return null;
  if (!PANEL_KINDS.includes(field.kind)) return null;
  if (field.kind === "entity" && field.multiple) return null;
  return field;
}

/** Nothing for the person to read or decide: the change can be applied as soon as the dry run says so. */
export function isQuietDryRun(dryRun: DryRun): boolean {
  return (
    dryRun.can_commit &&
    dryRun.errors.length === 0 &&
    dryRun.checks.length === 0 &&
    dryRun.choices.length === 0 &&
    dryRun.confirmations.length === 0 &&
    dryRun.display.length === 0
  );
}

/**
 * A first dry run, before any input, that already asks the person something (a choice to make, something to confirm):
 * the panel has nothing useful to offer first, so it hands over to the dialog.
 */
export function asksBeforeAnyInput(dryRun: DryRun): boolean {
  return dryRun.choices.length > 0 || dryRun.confirmations.length > 0;
}
